import {Skeleton, Stack} from '@mui/material';
import {translations} from '../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {
  EntityOccurrenceType,
  EntityOccurrenceTypeCategory,
} from '../../../../backend/responses/entity-occurrence';
import {useEffect, useState} from 'react';
import {EntityOccurrencesRepository} from '../../../../repositories/entity-occurrences';
import {Controller, useForm} from 'react-hook-form';
import {DailyCareInput} from '../../../../components/daily-care-log/forms/daily-care-input';
import {DailyCareSelect} from '../../../../components/daily-care-log/forms/daily-care-select';
import {DailyCareTimeInput} from '../../../../components/daily-care-log/forms/daily-care-time-input';
import {DailyCareDateInput} from '../../../../components/daily-care-log/forms/daily-care-date-input';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {DEFAULT_FORM_VALIDATIONS} from '../../../../utils/constants/forms';
import {RESPONSIBLE_NOT_DEFINED} from '../../office/professional-caregiver/professional-caregivers/shifts/actions-shift-flow/actions-shift-form';
import {
  AddAttachmentsForm,
  SavingAttachment,
} from '../../../../components/daily-care-log/attachments/add-attachments-form';
import {sortOccurrenceTypesByName} from '../../../../utils/data/occurrences';

export type AddOccurrenceFormData = {
  occurrenceType: string;
  occurrenceDate: Date;
  occurrenceTime: Date;
  occurrenceLocal: string;
  occurrencePatient: string;
  occurrenceResponsible: string;
  occurrenceDetails: string;
  occurrenceFiles?: SavingAttachment[];
};

type AddOccurrenceFormProps = {
  entityId: string;
  onFormChange: (
    state: AddOccurrenceFormData,
    errors: boolean,
    category?: EntityOccurrenceTypeCategory
  ) => void;
  seniors?: AffiliatedUser[];
  professionals?: AffiliatedUser[];
};

export const AddOccurrenceForm = ({
  entityId,
  seniors,
  professionals,
  onFormChange,
}: AddOccurrenceFormProps) => {
  const {t} = useTranslation();
  const [occurrenceTypes, setOccurrenceTypes] = useState<EntityOccurrenceType[] | undefined>(
    undefined
  );
  const {
    control,
    setValue,
    reset,
    watch,
    register,
    setError,
    clearErrors,
    formState: {errors},
    getValues,
  } = useForm<AddOccurrenceFormData>({
    ...DEFAULT_FORM_VALIDATIONS,
  });
  const [category, setCategory] = useState<EntityOccurrenceTypeCategory>();
  const [
    occurrenceType,
    occurrenceDate,
    occurrenceTime,
    occurrenceLocal,
    occurrencePatient,
    occurrenceResponsible,
    occurrenceDetails,
    occurrenceFiles,
  ] = watch([
    'occurrenceType',
    'occurrenceDate',
    'occurrenceTime',
    'occurrenceLocal',
    'occurrencePatient',
    'occurrenceResponsible',
    'occurrenceDetails',
    'occurrenceFiles',
  ]);

  useEffect(() => {
    Promise.all([EntityOccurrencesRepository.instance.getOccurrencesTypes(entityId)]).then(
      ([occurrencesTypes]: [EntityOccurrenceType[]]) => {
        setOccurrenceTypes(
          sortOccurrenceTypesByName(occurrencesTypes.filter((type) => type.summary.isActive))
        );
      }
    );
  }, [entityId]);

  useEffect(() => {
    const defaultValues: AddOccurrenceFormData = {
      occurrenceType: '',
      occurrenceDate: new Date(),
      occurrenceTime: new Date(),
      occurrenceLocal: '',
      occurrencePatient: '',
      occurrenceResponsible: '',
      occurrenceDetails: '',
      occurrenceFiles: undefined,
    };
    reset(defaultValues);
  }, [reset]);

  useEffect(() => {
    if (category) {
      onFormChange(getValues(), Object.keys(errors).length > 0, category);
    } else {
      onFormChange(getValues(), true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    occurrenceDate,
    occurrenceDetails,
    occurrenceLocal,
    occurrencePatient,
    occurrenceResponsible,
    occurrenceTime,
    occurrenceType,
    occurrenceFiles,
    category,
    getValues,
  ]);

  if (!occurrenceTypes) {
    return (
      <>
        <Stack gap={2} flexDirection="row" width="100%">
          <Skeleton
            sx={{
              height: '50px',
              width: '50%',
            }}
          />
          <Skeleton
            sx={{
              height: '50px',
              width: '50%',
            }}
          />
        </Stack>
        <Stack gap={1} flexDirection="column" width="100%">
          <Skeleton
            sx={{
              height: '80px',
              width: '100%',
            }}
          />
          <Skeleton
            sx={{
              height: '100px',
              width: '100%',
            }}
          />
        </Stack>
      </>
    );
  }

  return (
    <form>
      <Controller
        name="occurrenceType"
        control={control}
        rules={{required: true}}
        render={({field: {value}}) => (
          <DailyCareSelect
            label={t(translations.daily_care_log.occurrences.type)}
            required
            options={
              occurrenceTypes.map((occurrenceType) => {
                return {
                  key: occurrenceType.occurrenceType.id,
                  label: occurrenceType.occurrenceType.name,
                  category: occurrenceType.occurrenceType.category,
                };
              }) || []
            }
            categories={[
              {
                key: 'patients',
                label: t(translations.office.professional_caregiver.settings.occurrences.patients),
              },
              {
                key: 'general',
                label: t(translations.office.professional_caregiver.settings.occurrences.general),
              },
            ]}
            value={value}
            onChange={(value) => {
              const selected = occurrenceTypes.find(
                (type) => type.occurrenceType.id === value.target.value
              );
              setCategory(selected?.occurrenceType.category);
              setValue('occurrenceType', value.target.value as string);
            }}
          />
        )}
      />
      <Controller
        name="occurrenceDate"
        control={control}
        rules={{required: true}}
        render={({field: {value}}) => (
          <DailyCareDateInput
            value={value}
            required
            label={t(translations.daily_care_log.occurrences.date)}
            onChange={(value) => {
              if (value) setValue('occurrenceDate', value);
            }}
          />
        )}
      />
      <Controller
        name="occurrenceTime"
        control={control}
        rules={{required: true}}
        render={({field: {value}}) => (
          <DailyCareTimeInput
            value={value}
            required
            label={t(translations.daily_care_log.occurrences.time)}
            onChange={(value) => {
              if (value) setValue('occurrenceTime', value);
            }}
          />
        )}
      />
      <Controller
        name="occurrenceLocal"
        control={control}
        render={() => (
          <DailyCareInput
            {...register('occurrenceLocal', {
              maxLength: {
                value: 35,
                message: t(translations.daily_care_log.occurrences.local_error, {
                  characters: 35,
                }),
              },
            })}
            label={t(translations.daily_care_log.occurrences.local)}
            errors={errors.occurrenceLocal?.message}
          />
        )}
      />
      {category === 'patients' && (
        <Controller
          name="occurrencePatient"
          control={control}
          rules={{required: category === 'patients'}}
          disabled={!seniors}
          render={({field: {value}}) => (
            <DailyCareSelect
              label={t(translations.daily_care_log.occurrences.patient)}
              required
              user
              options={
                seniors?.map((caregiver, index) => {
                  return {
                    key: caregiver.user.id || index.toString(),
                    label: caregiver.user.name,
                    picture: caregiver.user.picture,
                    border:
                      caregiver.entitySocialResponses.length > 0
                        ? caregiver.entitySocialResponses[0].foregroundColor
                        : undefined,
                  };
                }) || []
              }
              value={value}
              onChange={(value) => setValue('occurrencePatient', value.target.value as string)}
            />
          )}
        />
      )}
      <Controller
        name="occurrenceResponsible"
        control={control}
        rules={{required: true}}
        render={({field: {value}}) => (
          <DailyCareSelect
            label={t(translations.daily_care_log.occurrences.responsible)}
            user
            disabled={!professionals}
            options={
              !professionals
                ? []
                : [
                    {
                      key: RESPONSIBLE_NOT_DEFINED,
                      label: t(
                        translations.shifts_page.shift_modal.shift_form
                          .shift_responsible_placeholder
                      ),
                    },
                    ...professionals.map((user, index) => {
                      return {
                        key: user.user.id || index.toString(),
                        label: user.user.name,
                        picture: user.user.picture,
                      };
                    }),
                  ]
            }
            value={value}
            onChange={(value) => setValue('occurrenceResponsible', value.target.value as string)}
          />
        )}
      />
      <Controller
        name="occurrenceDetails"
        control={control}
        render={({field: {value}}) => (
          <DailyCareInput
            multiline
            required
            rows={3}
            value={value}
            label={t(translations.daily_care_log.occurrences.details)}
            onChange={(event) => setValue('occurrenceDetails', event.target.value)}
          />
        )}
      />
      <Controller
        name="occurrenceFiles"
        control={control}
        render={({field: {value}}) => {
          return (
            <AddAttachmentsForm
              type="upload"
              attachments={value}
              onAttachmentsChange={(value) => setValue('occurrenceFiles', value)}
              onError={(errors) => {
                if (errors.length > 0) {
                  setError('occurrenceFiles', {message: 'Error'});
                } else {
                  clearErrors('occurrenceFiles');
                }
              }}
            />
          );
        }}
      />
    </form>
  );
};
